'use client';

import Link from 'next/link';
import { PortableText, PortableTextBlock } from 'next-sanity';
import { stegaClean } from '@sanity/client/stega';
import { GroqImage } from '@/helpers/Image';
import { richTextComponent } from '@/sanity/sanityPortableText';
import { Button } from '@/shadcn/ui/button';
import {
  BlogArticleCard,
  BlogCardBackground,
} from '@/sanity/pages/PageBlogOverviewTemplate';

/**
 * Splits a richtext value into heading blocks (h1-h6) and paragraph blocks
 * (normal/other). Used by the "centered" layout to render the heading on the
 * left column and the descriptive paragraph on the right column.
 */
function splitTitleBlocks(blocks?: PortableTextBlock[]): {
  headings: PortableTextBlock[];
  paragraphs: PortableTextBlock[];
} {
  if (!blocks?.length) return { headings: [], paragraphs: [] };
  const headings: PortableTextBlock[] = [];
  const paragraphs: PortableTextBlock[] = [];
  for (const block of blocks) {
    const b = block as any;
    const style = stegaClean(b?.style ?? '');
    if (b?._type === 'block' && style && style !== 'normal') {
      headings.push(block);
    } else {
      paragraphs.push(block);
    }
  }
  return { headings, paragraphs };
}

interface CardTheme {
  bg: string;
  category: string;
  categoryLine: string;
  divider: string;
  date: string;
  /** Prose classes to color <h1>-<h6> and <p> inside the richtext title. */
  titleProse: string;
  excerpt: string;
  ctaLine: string;
  ctaText: string;
  ctaArrow: string;
}

const themes: Record<BlogCardBackground, CardTheme> = {
  black: {
    bg: 'bg-black',
    category: 'text-[#f2f0ef]',
    categoryLine: 'bg-[#f2f0ef]',
    divider: 'bg-white/30',
    date: 'text-white/50',
    titleProse: 'prose-headings:text-[#dadada] prose-p:text-[#dadada]',
    excerpt: 'text-[#f2f0ef]/[72%]',
    ctaLine: 'bg-[#dadada]',
    ctaText: 'text-[#dadada]',
    ctaArrow: 'text-[#dadada]',
  },
  darkNavy: {
    bg: 'bg-[#1B2846]',
    category: 'text-[#f2f0ef]',
    categoryLine: 'bg-[#f2f0ef]',
    divider: 'bg-white/30',
    date: 'text-white/50',
    titleProse: 'prose-headings:text-[#dadada] prose-p:text-[#dadada]',
    excerpt: 'text-[#f2f0ef]/[72%]',
    ctaLine: 'bg-[#dadada]',
    ctaText: 'text-[#dadada]',
    ctaArrow: 'text-[#dadada]',
  },
  white: {
    bg: 'bg-white',
    category: 'text-[#1B2846]',
    categoryLine: 'bg-[#1B2846]',
    divider: 'bg-black/30',
    date: 'text-black/50',
    titleProse: 'prose-headings:text-[#1B2846] prose-p:text-[#1B2846]',
    excerpt: 'text-black/[72%]',
    ctaLine: 'bg-[#1B2846]',
    ctaText: 'text-[#1B2846]',
    ctaArrow: 'text-[#1B2846]',
  },
  offwhite: {
    bg: 'bg-[#F2F0EF]',
    category: 'text-[#1B2846]',
    categoryLine: 'bg-[#1B2846]',
    divider: 'bg-black/30',
    date: 'text-black/50',
    titleProse: 'prose-headings:text-[#1B2846] prose-p:text-[#1B2846]',
    excerpt: 'text-black/[72%]',
    ctaLine: 'bg-[#1B2846]',
    ctaText: 'text-[#1B2846]',
    ctaArrow: 'text-[#1B2846]',
  },
};

function ChipWithLine({
  chip,
  isCenter,
  theme,
}: {
  chip: string;
  isCenter: boolean;
  theme: CardTheme;
}) {
  return (
    <div
      className={`flex items-center gap-[1rem] lg:gap-[2rem] ${isCenter ? 'justify-center' : 'justify-start lg:justify-start'}`}
    >
      <span
        aria-hidden
        className={`block h-[0.125rem] w-[14.75rem] lg:w-[18.75rem] ${theme.categoryLine}`}
      />
      <p
        className={`mb-0 font-nunito font-normal lg:font-light uppercase text-[1.25rem] lg:text-[1.625rem] leading-none tracking-[0.4125rem] lg:tracking-[0.53625rem] ${theme.category}`}
      >
        {chip}
      </p>
    </div>
  );
}

function ArticleHeader({
  chipLabel,
  title,
  theme,
  className = '',
}: {
  chipLabel?: string;
  title?: BlogArticleCard['title'];
  theme: CardTheme;
  className?: string;
}) {
  return (
    <div
      className={`flex flex-col gap-[3rem] lg:gap-[5rem] w-full ${className}`}
    >
      {chipLabel && (
        <ChipWithLine chip={chipLabel} isCenter={false} theme={theme} />
      )}
      {title && (
        <div
          className={`richtext flex flex-col gap-[5rem] text-left
            prose-headings:mb-0 prose-p:mb-0
            prose-headings:font-nunito prose-headings:font-[200] prose-headings:uppercase prose-headings:leading-[1.2] prose-headings:tracking-[-0.1rem]
            prose-h1:text-[4.5rem] lg:prose-h1:text-[7rem]
            prose-h2:text-[4.5rem] lg:prose-h2:text-[7rem]
            prose-em:font-newsreader prose-em:font-[300] prose-em:italic
            ${theme.titleProse}`}
        >
          <PortableText value={title} components={richTextComponent} />
        </div>
      )}
    </div>
  );
}

function DiscoverMoreCTA({
  href,
  dark = false,
}: {
  href: string;
  dark?: boolean;
}) {
  // On light card backgrounds, override the default button's white text/line/svg
  // to dark navy (#1B2846) — scoped to this element, doesn't affect other buttons.
  const darkOverride = dark
    ? '!text-[#1B2846] [&_.button-default-left-bar]:!bg-[#1B2846] [&_.button-default-right-bar_svg_path]:!fill-[#1B2846]'
    : '';

  return (
    <Link
      href={href}
      className="group inline-block relative z-10"
      data-track-action="button-click"
    >
      <Button
        variant="default"
        type="button"
        className={`relative w-fit flex items-center justify-center overflow-clip ${darkOverride}`}
      >
        <div className="button-bg absolute inset-0 duration-500 transition-all" />
        <div className="relative leading-none">
          <div className="button-text">Discover more</div>
        </div>
      </Button>
    </Link>
  );
}

function ArticleMedia({ article }: { article: BlogArticleCard }) {
  const { mainImage } = article;
  if (!mainImage?.src) {
    return <div className="!w-full !h-full bg-white/5" />;
  }
  return (
    <GroqImage
      src={mainImage}
      alt={mainImage.name ?? ''}
      className="!w-full !h-full !object-cover"
    />
  );
}

function ArticleTextContent({
  article,
  theme,
  href,
  isLightBg,
  hideHeader = false,
}: {
  article: BlogArticleCard;
  theme: CardTheme;
  href: string;
  isLightBg: boolean;
  /** When true, chip + title are hidden on mobile (still shown on desktop). */
  hideHeader?: boolean;
}) {
  const { title, chip, category } = article;
  const chipLabel = chip ?? category;

  return (
    <div className="flex flex-col gap-[4rem] lg:gap-[5rem] w-full items-start lg:items-start">
      {chipLabel && title && (
        <ArticleHeader
          chipLabel={chipLabel}
          title={title}
          theme={theme}
          className={hideHeader ? 'hidden lg:flex' : ''}
        />
      )}

      <DiscoverMoreCTA href={href} dark={isLightBg} />
    </div>
  );
}

export default function ArticleCard({ article }: { article: BlogArticleCard }) {
  const layoutVariant =
    (stegaClean(article.layoutVariant) as BlogArticleCard['layoutVariant']) ??
    'centered';
  const backgroundColor: BlogCardBackground =
    (stegaClean(article.backgroundColor) as BlogCardBackground) ?? 'black';
  const href = article.slug ?? '#';
  const theme = themes[backgroundColor] ?? themes.black;
  const isLightBg =
    backgroundColor === 'white' || backgroundColor === 'offwhite';

  const chipLabel = article.chip ?? article.category;

  // Side-by-side variants — based on MinsterBezelSection
  if (layoutVariant === 'imageLeft' || layoutVariant === 'imageRight') {
    const isImageRight = layoutVariant === 'imageRight';
    return (
      <article
        className={`group lg:min-h-[135rem] lg:max-w-none flex flex-col lg:items-stretch items-stretch gap-[3rem] lg:gap-[11.62rem] px-0 py-[3rem] lg:py-0 ${theme.bg} ${
          isImageRight ? 'lg:flex-row-reverse' : 'lg:flex-row'
        }`}
      >
        {/* Mobile-only header (chip + title) — hidden on lg */}
        <div className="lg:hidden px-[2rem]">
          <ArticleHeader
            chipLabel={chipLabel}
            title={article.title}
            theme={theme}
          />
        </div>

        <div className="w-full lg:w-[60%] lg:overflow-hidden">
          <ArticleMedia article={article} />
        </div>
        <div className="flex flex-col gap-[5rem] w-full lg:w-[68rem] px-[2rem] lg:px-0 items-start lg:items-start lg:justify-center">
          <ArticleTextContent
            article={article}
            theme={theme}
            href={href}
            isLightBg={isLightBg}
            hideHeader
          />
        </div>
      </article>
    );
  }

  // Centered variant — chip on top, title left / paragraph right, CTA below, image at bottom
  const { headings: headingBlocks, paragraphs: paragraphBlocks } =
    splitTitleBlocks(article.title as PortableTextBlock[] | undefined);
  const hasSplit = headingBlocks.length > 0 && paragraphBlocks.length > 0;
  const singleColumnBlocks = hasSplit
    ? undefined
    : (article.title ?? paragraphBlocks);
  const paragraphColorClass = isLightBg
    ? 'prose-p:!text-[#1B2846]/[72%]'
    : 'prose-p:!text-[#f2f0ef]/[72%]';

  return (
    <article
      className={`group lg:max-w-none flex flex-col items-center gap-[6rem] lg:gap-[10rem] px-0  ${theme.bg}`}
    >
      <div className="flex flex-col gap-[6rem] lg:gap-[10rem] w-full container items-start lg:items-center">
        {article.title &&
          (hasSplit ? (
            <div className="w-full pt-[6rem] lg:pt-[10rem]  flex lg:flex-row flex-col justify-between gap-[6rem] lg:gap-[8rem] items-start lg:items-center">
              <div
                className={`richtext items-start flex flex-col gap-[5rem] text-left
                  prose-headings:mb-0 prose-p:mb-0 lg:w-[90rem]
                  prose-headings:font-nunito prose-headings:font-[200] prose-headings:uppercase prose-headings:leading-[1.2] prose-headings:tracking-[-0.1rem]
                  prose-h1:text-[4.5rem] lg:prose-h1:text-[7rem]
                  prose-h2:text-[4.5rem] lg:prose-h2:text-[7rem]
                  prose-em:font-newsreader prose-em:font-[300] prose-em:italic
                  ${theme.titleProse}`}
              >
                {chipLabel && (
                  <ChipWithLine chip={chipLabel} isCenter theme={theme} />
                )}
                <PortableText
                  value={headingBlocks as PortableTextBlock[]}
                  components={richTextComponent}
                />
              </div>

              {/* Mobile-only image between title and paragraph */}
              <div className="lg:hidden w-full h-[54.4rem] overflow-hidden">
                <ArticleMedia article={article} />
              </div>

              <div
                className={`richtext flex flex-col gap-[3rem] items-start text-left max-w-[55.75rem]
                  prose-p:mb-0 prose-p:font-nunito prose-p:font-[300] prose-p:text-[2rem] prose-p:leading-[1.6]
                  ${paragraphColorClass}`}
              >
                <PortableText
                  value={paragraphBlocks as PortableTextBlock[]}
                  components={richTextComponent}
                />
                <DiscoverMoreCTA href={href} dark={isLightBg} />
              </div>
            </div>
          ) : (
            <div
              className={`richtext flex flex-col gap-[5rem] text-left w-full
                prose-headings:mb-0 prose-p:mb-0
                prose-headings:font-nunito prose-headings:font-[200] prose-headings:uppercase prose-headings:leading-[1.2] prose-headings:tracking-[-0.1rem]
                prose-h1:text-[4.5rem] lg:prose-h1:text-[7rem]
                prose-h2:text-[4.5rem] lg:prose-h2:text-[7rem]
                prose-em:font-newsreader prose-em:font-[300] prose-em:italic
                ${theme.titleProse}`}
            >
              <PortableText
                value={(singleColumnBlocks ?? []) as PortableTextBlock[]}
                components={richTextComponent}
              />
            </div>
          ))}
      </div>

      {/* Desktop-only image below both columns */}
      <div className="hidden lg:block w-full h-[90rem] overflow-hidden ">
        <ArticleMedia article={article} />
      </div>
    </article>
  );
}
